import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { queryClient } from '../../app/query-client';
import { useAuth } from '../../auth/AuthProvider';
import { permissions } from '../../auth/permissions';
import { Route42Logo } from '../brand/Route42Logo';
import { MachineSwitcher } from './MachineSwitcher';
import { NotificationBell } from '../../features/notifications/components/NotificationBell';
import { applyTheme, getThemePreference, type ThemePreference } from '../../utils/theme';

export function TopBar({
  machineId,
  onMenu,
  showNavigation = true,
  showOrganizationSwitch = true,
}: {
  machineId: string | null;
  onMenu: () => void;
  showNavigation?: boolean;
  showOrganizationSwitch?: boolean;
}) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [theme, setTheme] = useState<ThemePreference>(() => getThemePreference());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  async function handleSignOut() {
    queryClient.clear();
    await auth.signOut();
    navigate('/sign-in', { replace: true });
  }

  const organizationName = auth.activeMembership?.customer_name?.trim() || 'Organization';

  const initials = (auth.profile?.user.email ?? auth.profile?.user.username ?? 'R42')
    .split(/[@.\s_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'R42';

  return (
    <header className="r42-topbar">
      {showNavigation ? (
        <button className="r42-icon-button r42-mobile-menu" onClick={onMenu} aria-label="Open navigation">
          <i className="bi bi-list" aria-hidden="true" />
        </button>
      ) : null}
      <Link className="r42-brand" to="/machines" aria-label="Route42 home">
        <Route42Logo variant="responsive" />
      </Link>
      {machineId ? <MachineSwitcher machineId={machineId} /> : <div className="r42-topbar-spacer" />}
      {showOrganizationSwitch && auth.activeCustomerId ? (
        <Link
          className="r42-topbar-organization"
          to="/organizations"
          aria-label={`Change organization (current: ${organizationName})`}
          title={`Change organization · ${organizationName}`}
        >
          <i className="bi bi-building" aria-hidden="true" />
          <span className="r42-topbar-organization__name">{organizationName}</span>
          <i className="bi bi-chevron-down r42-topbar-organization__chevron" aria-hidden="true" />
        </Link>
      ) : null}
      {showNavigation ? <NotificationBell /> : null}
      <div className="dropdown">
        <button className="r42-icon-button" data-bs-toggle="dropdown" aria-label="Appearance">
          <i className="bi bi-circle-half" aria-hidden="true" />
        </button>
        <ul className="dropdown-menu dropdown-menu-end">
          {(['system', 'light', 'dark'] as ThemePreference[]).map((value) => (
            <li key={value}>
              <button className={`dropdown-item${theme === value ? ' active' : ''}`} onClick={() => setTheme(value)}>
                {value === 'system' ? 'System' : value[0].toUpperCase() + value.slice(1)}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="dropdown">
        <button className="r42-avatar" data-bs-toggle="dropdown" aria-label="User menu">{initials}</button>
        <ul className="dropdown-menu dropdown-menu-end">
          <li className="px-3 py-2">
            <strong className="d-block small">{auth.profile?.user.email ?? 'Route42 user'}</strong>
            <small className="text-secondary">{auth.activeMembership?.customer_name ?? 'Route42'}</small>
          </li>
          <li><hr className="dropdown-divider" /></li>
          {auth.activeCustomerId ? (
            <li><Link className="dropdown-item" to="/organizations">Change organization</Link></li>
          ) : null}
          <li><Link className="dropdown-item" to="/help"><i className="bi bi-question-circle me-2" aria-hidden="true" />Help Center</Link></li>
          <li><Link className="dropdown-item" to="/settings/profile">Profile</Link></li>
          {auth.can(permissions.organizationRead) ? (
            <li><Link className="dropdown-item" to="/settings/organization">Organization</Link></li>
          ) : null}
          {auth.can(permissions.usersRead) ? (
            <li><Link className="dropdown-item" to="/settings/users">Users & access</Link></li>
          ) : null}
          {auth.can(permissions.billingRead) ? (
            <li><Link className="dropdown-item" to="/settings/billing">Billing</Link></li>
          ) : null}
          <li><hr className="dropdown-divider" /></li>
          <li><button className="dropdown-item" onClick={() => void handleSignOut()}>Sign out</button></li>
        </ul>
      </div>
    </header>
  );
}
